import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft, CreditCard, RefreshCw, Save, DollarSign, TrendingUp,
  Globe, Check, X, AlertTriangle, Info, Settings2, Wallet, Percent,
  Sun, Moon
} from 'lucide-react';
import { AdminSidebar, AdminSidebarToggle } from '@/components/admin/AdminSidebar';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icônes des moyens de paiement
const PaymentIcon = ({ id }) => {
  switch (id) {
    case 'stripe':
    case 'credit-card':
      return <CreditCard className="w-5 h-5" />;
    case 'apple_pay':
    case 'apple':
      return (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
        </svg>
      );
    case 'google_pay':
    case 'google':
      return (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
      );
    case 'paypal':
      return (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#003087">
          <path d="M19.554 9.488c.121.563.106 1.246-.04 2.051-.582 2.978-2.477 4.466-5.683 4.466h-.442a.666.666 0 0 0-.659.569l-.553 3.505-.15.951a.333.333 0 0 1-.33.286H8.334a.333.333 0 0 1-.33-.384l.12-.76.023-.15.44-2.79.03-.185a.333.333 0 0 1 .33-.286h.78c2.673 0 4.767-.99 5.38-3.853.256-1.2.122-2.2-.367-2.916.17.108.333.228.483.36.533.469.884 1.133.884 1.133" fill="#009cde"/>
          <path d="M19.486 7.49a5.002 5.002 0 0 0-.62-.62 5.003 5.003 0 0 0-1.314-.74 7.006 7.006 0 0 0-2.21-.325h-4.747a.666.666 0 0 0-.66.569l-1.983 12.58a.333.333 0 0 0 .33.384h2.666l.667-4.245-.02.134a.666.666 0 0 1 .659-.569h1.371c2.673 0 4.767-.99 5.38-3.853.018-.085.034-.17.048-.252.181-1.16.021-1.948-.567-2.663" fill="#012169"/>
        </svg>
      );
    case 'bit':
      return <Wallet className="w-5 h-5 text-green-500" />;
    case 'klarna':
      return (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#FFB3C7">
          <rect width="24" height="24" rx="4"/>
          <text x="4" y="17" fill="#000" fontSize="10" fontWeight="bold">K</text>
        </svg>
      );
    default:
      return <CreditCard className="w-5 h-5" />;
  }
};

export function AdminPaymentsPage() {
  const navigate = useNavigate();
  const { isLight, toggleTheme } = useAdminTheme();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState('');
  const [activeTab, setActiveTab] = useState('methods');
  
  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [integrationsStatus, setIntegrationsStatus] = useState({});
  
  // Currency state
  const [currencyRates, setCurrencyRates] = useState({});
  const [currencyConfig, setCurrencyConfig] = useState({
    margin: 2.0,
    default_currency: 'ILS',
    auto_detect: true
  });
  
  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [transactionStats, setTransactionStats] = useState({});

  // Check authentication
  useEffect(() => {
    const storedToken = localStorage.getItem('admin_token');
    if (storedToken) {
      setToken(storedToken);
      setIsAuthenticated(true);
      fetchData(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchData = async (authToken) => {
    try {
      // Fetch payment methods (admin)
      const methodsRes = await fetch(`${API_URL}/api/payments/admin/methods`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (methodsRes.ok) {
        const data = await methodsRes.json();
        setPaymentMethods(data.methods || []);
        setIntegrationsStatus(data.integrations_status || {});
      }
      
      // Fetch currency rates
      const ratesRes = await fetch(`${API_URL}/api/currency/rates`);
      if (ratesRes.ok) {
        const data = await ratesRes.json();
        setCurrencyRates(data);
      }
      
      // Fetch currency config (admin)
      const configRes = await fetch(`${API_URL}/api/currency/config`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (configRes.ok) {
        const data = await configRes.json();
        setCurrencyConfig(data);
      }
      
      // Fetch transactions
      const txRes = await fetch(`${API_URL}/api/payments/admin/transactions?limit=20`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (txRes.ok) {
        const data = await txRes.json();
        setTransactions(data.transactions || []);
        setTransactionStats(data.stats || {});
      }
      
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Erreur de chargement');
    } finally {
      setIsLoading(false);
    }
  };

  const updatePaymentMethod = (id, field, value) => {
    setPaymentMethods(prev => prev.map(m => 
      m.id === id ? { ...m, [field]: value } : m
    ));
  };

  const savePaymentMethods = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/admin/methods`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentMethods)
      });
      
      if (!response.ok) throw new Error('Failed to save');
      toast.success('Configuration des paiements enregistrée');
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const saveCurrencyConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/currency/config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(currencyConfig)
      });
      
      if (!response.ok) throw new Error('Failed to save');
      toast.success('Configuration des devises enregistrée');
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const refreshRates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/currency/rates/refresh`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to refresh');
      const data = await response.json();
      setCurrencyRates({ ...currencyRates, rates: data.rates, updated_at: data.updated_at });
      toast.success('Taux de change mis à jour');
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center">
        <Card className="w-full max-w-md bg-zinc-900/80 border-zinc-800">
          <CardHeader className="text-center">
            <CardTitle className="text-xl text-white">Accès Admin Requis</CardTitle>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/admin')} className="w-full">
              Aller au Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn(
      "min-h-screen transition-colors duration-200",
      isLight ? "bg-gray-100 admin-light-theme" : "bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950"
    )}>
      {/* Sidebar */}
      <AdminSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(false)} />
      
      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : ''}`}>
        {/* Header */}
        <header className={cn(
          "border-b backdrop-blur-sm sticky top-0 z-40",
          isLight ? "border-gray-200 bg-white/90" : "border-zinc-800 bg-zinc-900/50"
        )}>
          <div className="px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <AdminSidebarToggle onClick={() => setSidebarOpen(true)} />
              <div>
                <h1 className={cn("text-xl font-bold", isLight ? "text-gray-900" : "text-white")}>Paramètres de Paiement</h1>
                <p className={cn("text-sm", isLight ? "text-gray-600" : "text-gray-400")}>Configurez les moyens de paiement et les devises</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={toggleTheme}
                className={cn(
                  "gap-2 transition-colors",
                  isLight 
                    ? "border-gray-300 bg-white hover:bg-gray-100 text-gray-700" 
                    : "border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-gray-300"
                )}
              >
                {isLight ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                <span className="hidden sm:inline">{isLight ? 'Sombre' : 'Clair'}</span>
              </Button>
            </div>
          </div>
        </header>

      <main className="p-4 md:p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-zinc-800/50 p-1 grid grid-cols-3 w-full max-w-xl">
            <TabsTrigger value="methods" className="data-[state=active]:bg-primary">
              <CreditCard className="w-4 h-4 mr-2" />
              Paiements
            </TabsTrigger>
            <TabsTrigger value="currency" className="data-[state=active]:bg-primary">
              <DollarSign className="w-4 h-4 mr-2" />
              Devises
            </TabsTrigger>
            <TabsTrigger value="transactions" className="data-[state=active]:bg-primary">
              <TrendingUp className="w-4 h-4 mr-2" />
              Transactions
            </TabsTrigger>
          </TabsList>

          {/* Tab: Payment Methods */}
          <TabsContent value="methods" className="space-y-6">
            {/* Status cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-zinc-900/60 border-zinc-800">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${integrationsStatus.stripe ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                    {integrationsStatus.stripe ? <Check className="w-6 h-6 text-green-400" /> : <X className="w-6 h-6 text-red-400" />}
                  </div>
                  <div>
                    <p className="text-sm text-white">Stripe</p>
                    <p className="text-lg font-semibold text-white">
                      {integrationsStatus.stripe ? 'Connecté' : 'Non configuré'}
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-zinc-900/60 border-zinc-800">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${integrationsStatus.paypal ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
                    {integrationsStatus.paypal ? <Check className="w-6 h-6 text-green-400" /> : <AlertTriangle className="w-6 h-6 text-yellow-400" />}
                  </div>
                  <div>
                    <p className="text-sm text-white">PayPal</p>
                    <p className="text-lg font-semibold text-white">
                      {integrationsStatus.paypal ? 'Connecté' : 'Sandbox requis'}
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-zinc-900/60 border-zinc-800">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Info className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-white">Apple/Google Pay</p>
                    <p className="text-lg font-semibold text-white">Via Stripe</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Payment methods list */}
            <Card className="bg-zinc-900/60 border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white">Moyens de paiement</CardTitle>
                  <CardDescription>Activez et configurez les moyens de paiement</CardDescription>
                </div>
                <Button onClick={savePaymentMethods} size="sm" className="bg-primary">
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {paymentMethods.map((method) => (
                  <div 
                    key={method.id}
                    className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg border border-zinc-700"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center">
                        <PaymentIcon id={method.icon || method.id} />
                      </div>
                      <div>
                        <p className="font-medium text-white">{method.name}</p>
                        {method.note && (
                          <p className="text-xs text-white">{method.note}</p>
                        )}
                        {method.countries && method.countries.length > 0 && (
                          <p className="text-xs text-gray-200">Pays: {method.countries.join(', ')}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {method.configured ? (
                        <Badge className="bg-green-500/20 text-green-400">Configuré</Badge>
                      ) : method.api_key_required ? (
                        <Badge className="bg-yellow-500/20 text-yellow-400">Clé requise</Badge>
                      ) : null}
                      
                      {method.min_amount > 0 && (
                        <span className="text-xs text-white">Min: {method.min_amount}€</span>
                      )}
                      
                      <Switch
                        checked={method.enabled}
                        onCheckedChange={(checked) => updatePaymentMethod(method.id, 'enabled', checked)}
                        disabled={!method.configured && method.api_key_required}
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Currency */}
          <TabsContent value="currency" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Current rates */}
              <Card className="bg-zinc-900/60 border-zinc-800">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Taux de change</CardTitle>
                    <CardDescription>
                      Base: USD | Mis à jour: {currencyRates.updated_at ? new Date(currencyRates.updated_at).toLocaleString('fr-FR') : 'N/A'}
                    </CardDescription>
                  </div>
                  <Button onClick={refreshRates} size="sm" variant="outline">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualiser
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(currencyRates.rates || {}).map(([currency, rate]) => (
                      <div key={currency} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                        <span className="font-mono text-white">{currency}</span>
                        <span className="text-white">{rate}</span>
                      </div>
                    ))}
                  </div>
                  {currencyRates.margin > 0 && (
                    <p className="mt-4 text-sm text-amber-400 flex items-center gap-2">
                      <Percent className="w-4 h-4" />
                      Marge appliquée: +{currencyRates.margin}%
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Currency config */}
              <Card className="bg-zinc-900/60 border-zinc-800">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Configuration</CardTitle>
                    <CardDescription>Paramètres des devises</CardDescription>
                  </div>
                  <Button onClick={saveCurrencyConfig} size="sm" className="bg-primary">
                    <Save className="w-4 h-4 mr-2" />
                    Enregistrer
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-white">Marge sur le taux (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="20"
                      value={currencyConfig.margin}
                      onChange={(e) => setCurrencyConfig({ ...currencyConfig, margin: parseFloat(e.target.value) })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                    <p className="text-xs text-gray-200 mt-1">Ex: 2% = le client paie 2% de plus que le taux réel</p>
                  </div>
                  
                  <div>
                    <Label className="text-white">Devise par défaut</Label>
                    <select
                      value={currencyConfig.default_currency}
                      onChange={(e) => setCurrencyConfig({ ...currencyConfig, default_currency: e.target.value })}
                      className="w-full p-2 bg-zinc-800 border border-zinc-700 rounded-md text-white"
                    >
                      <option value="ILS">ILS - Shekel</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="USD">USD - Dollar</option>
                      <option value="GBP">GBP - Livre</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-white">Détection automatique</Label>
                      <p className="text-xs text-gray-200">Selon le pays du visiteur</p>
                    </div>
                    <Switch
                      checked={currencyConfig.auto_detect}
                      onCheckedChange={(checked) => setCurrencyConfig({ ...currencyConfig, auto_detect: checked })}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tab: Transactions */}
          <TabsContent value="transactions" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {Object.entries(transactionStats).map(([status, data]) => (
                <Card key={status} className="bg-zinc-900/60 border-zinc-800">
                  <CardContent className="p-4">
                    <p className="text-sm text-white capitalize">{status}</p>
                    <p className="text-2xl font-bold text-white">{data.count}</p>
                    <p className="text-sm text-gray-200">{data.total?.toFixed(2)} total</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Transactions list */}
            <Card className="bg-zinc-900/60 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Dernières transactions</CardTitle>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <p className="text-center text-white py-8">Aucune transaction</p>
                ) : (
                  <div className="space-y-2">
                    {transactions.map((tx, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Badge className={
                            tx.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' :
                            tx.payment_status === 'failed' ? 'bg-red-500/20 text-red-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }>
                            {tx.payment_status}
                          </Badge>
                          <div>
                            <p className="text-white font-mono text-sm">{tx.session_id?.slice(0, 20)}...</p>
                            <p className="text-xs text-gray-200">{tx.provider} • {tx.order_id}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-semibold">{tx.amount} {tx.currency?.toUpperCase()}</p>
                          <p className="text-xs text-gray-200">
                            {tx.created_at ? new Date(tx.created_at).toLocaleString('fr-FR') : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      </div>
    </div>
  );
}

export default AdminPaymentsPage;
